import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CONTACT_INFO, FOOTER_CONTENT } from '@/data/constants';
import { SOCIAL_LINKS } from '@/data/socialLinks';
import { useLanguage } from '@/contexts/LanguageContext';

const Footer: React.FC = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t } = useLanguage();

  const translateLinkLabel = (label: string) => {
    if (label.toLowerCase().includes('privacy')) return t('privacyPolicy');
    if (label.toLowerCase().includes('cancellation')) return t('cancellationRefundsPolicy');
    if (label.toLowerCase().includes('legal')) return t('legalDisclaimerCompliance');
    if (label.toLowerCase().includes('accessibility')) return t('accessibilityStatement');
    return label;
  };

  return (
    <footer ref={ref} className="relative">
      {/* Footer Content */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="py-12"
        style={{ backgroundColor: '#1F2A40' }}
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Organization Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-2xl font-serif font-bold text-white mb-6">
                {FOOTER_CONTENT.organizationName}
              </h3>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">{t('contactUs')}</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-primary-500 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-100 font-medium">9342767239</p>
                    <p className="text-sm text-gray-300">{t('mondayFriday')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-primary-500 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-100 font-medium">{CONTACT_INFO.email}</p>
                    <p className="text-sm text-gray-300">{t('generalInquiries')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-primary-500 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-100 font-medium">{t('emergency')}</p>
                    <p className="text-sm text-gray-300">9342767239</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Address & Office Hours */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">{t('officeLocation')}</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-primary-500 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-100 font-medium">{CONTACT_INFO.address.street}</p>
                    <p className="text-gray-100">
                      {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                    </p>
                    <p className="text-sm text-gray-300 mt-1">{t('india')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-primary-500 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-100 font-medium">{t('officeHours')}</p>
                    <p className="text-sm text-gray-300">{t('mondayFriday')}</p>
                    <p className="text-sm text-gray-300">{t('saturday')}</p>
                    <p className="text-sm text-gray-300">{t('sunday')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Links & Social */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">{t('quickLinks')}</h4>
              <div className="space-y-3">
                <div>
                  <div className="space-y-1">
                    {FOOTER_CONTENT.links.map((link, index) => (
                      <motion.div key={index} whileHover={{ x: 5 }}>
                        <Link
                          to={link.href}
                          className="block text-sm text-gray-200 hover:text-yellow-300 transition-colors duration-300 cursor-pointer"
                        >
                          {translateLinkLabel(link.label)}
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-100 mb-2">{t('followUs')}</h5>
                  <div className="flex flex-wrap gap-2">
                    {SOCIAL_LINKS.map((social) => (
                      <motion.a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        aria-label={social.name}
                        style={{ color: social.color }}
                        className="flex items-center justify-center"
                      >
                        {social.icon}
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Copyright */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 pt-8 border-t border-gray-300"
          >
            <div className="flex justify-center items-center">
              <p className="text-sm text-gray-200">
                @ All Rights Reserved 2026 / Powered by DIYA CHARITABLE TRUST / Get connected with us on Social Media Network
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>

    </footer>
  );
};

export default Footer;
